import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Mail, Eye, EyeOff, Loader2, AlertCircle, CheckCircle, Shield, User } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Founder credentials for display
const FOUNDER_EMAIL = 'founder@skbinteriors.co.uk';
const FOUNDER_PASSWORD = 'SKBFounder2024!';

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, signup, bootstrapFounder, isAuthenticated, loading: authLoading, needsOnboarding } = useAuth();

  const [isSignup, setIsSignup] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [founderExists, setFounderExists] = useState(null);
  const [checkingFounder, setCheckingFounder] = useState(true);
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      if (needsOnboarding) {
        navigate('/onboarding', { replace: true });
      } else {
        const from = location.state?.from?.pathname || '/command-centre';
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, authLoading, navigate, location, needsOnboarding]);

  // Check if Founder exists
  useEffect(() => {
    const checkFounder = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/founder-status`);
        if (response.ok) {
          const data = await response.json();
          setFounderExists(data.exists);
          if (data.exists) {
            // Pre-fill founder email
            setEmail(FOUNDER_EMAIL);
          }
        }
      } catch (err) {
        console.error('Failed to check founder status:', err);
      } finally {
        setCheckingFounder(false);
      }
    };
    checkFounder();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (isSignup) {
      // Handle signup
      if (!name.trim()) {
        setError('Please enter your name');
        setLoading(false);
        return;
      }
      
      const result = await signup(email, password, name);
      
      if (result.success) {
        setSuccess('Account created! Redirecting to onboarding...');
        setTimeout(() => {
          navigate('/onboarding');
        }, 500);
      } else {
        setError(result.error || 'Signup failed. Please try again.');
      }
    } else {
      // Handle login
      const result = await login(email, password, requires2FA ? twoFactorCode : null);

      if (result.success) {
        setSuccess('Login successful! Redirecting...');
        setTimeout(() => {
          if (result.needsOnboarding) {
            navigate('/onboarding');
          } else {
            navigate('/command-centre');
          }
        }, 500);
      } else if (result.requires_2fa) {
        setRequires2FA(true);
        setError('');
      } else {
        setError(result.error || 'Login failed. Please check your credentials.');
      }
    }

    setLoading(false);
  };
  
  const handleBack = () => {
    setRequires2FA(false);
    setTwoFactorCode('');
    setError('');
  };

  const handleBootstrapFounder = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    const result = await bootstrapFounder();

    if (result.success) {
      setSuccess(`Founder account ${result.data.created ? 'created' : 'activated'}! Redirecting...`);
      setTimeout(() => {
        navigate('/command-centre');
      }, 1000);
    } else {
      setError(result.error || 'Failed to bootstrap Founder account.');
    }

    setLoading(false);
  };

  const fillFounderCredentials = () => {
    setEmail(FOUNDER_EMAIL);
    setPassword(FOUNDER_PASSWORD);
    setIsSignup(false);
  };

  const toggleMode = () => {
    setIsSignup(!isSignup);
    setError('');
    setSuccess('');
    setName('');
    setRequires2FA(false);
  };

  if (authLoading || checkingFounder) {
    return (
      <div className="login-container" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent'
      }}>
        <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
      </div>
    );
  }

  return (
    <div className="login-container" style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'transparent',
      padding: 'var(--space-lg)'
    }} data-testid="login-page">
      <div className="tl-glass tl-glass-hero" style={{
        width: '100%',
        maxWidth: '420px',
        padding: 'var(--space-2xl)',
      }}>
        {/* Logo / Brand */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-xl)' }}>
          <BrandLogo showEmblem={true} variant="login" />
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginTop: 'var(--space-sm)' }}>
            {isSignup ? 'Create your account' : 'Sign in to your account'}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: 'var(--space-md)',
            background: 'var(--status-danger-bg)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-lg)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)'
          }} data-testid="login-error">
            <AlertCircle size={18} style={{ color: 'var(--status-danger)', flexShrink: 0 }} />
            <span style={{ color: 'var(--status-danger)', fontSize: '0.875rem' }}>{typeof error === 'string' ? error : 'An error occurred.'}</span>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div style={{
            padding: 'var(--space-md)',
            background: 'var(--status-success-bg)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-lg)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)'
          }} data-testid="login-success">
            <CheckCircle size={18} style={{ color: 'var(--status-success)', flexShrink: 0 }} />
            <span style={{ color: 'var(--status-success)', fontSize: '0.875rem' }}>{success}</span>
          </div>
        )}

        {/* Auth Form */}
        <form onSubmit={handleSubmit}>
          {!requires2FA ? (
            <>
              {/* Name Input (Signup only) */}
              {isSignup && (
                <div style={{ marginBottom: 'var(--space-lg)' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-xs)',
                    color: 'var(--text-secondary)',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}>
                    Full Name
                  </label>
                  <div style={{ position: 'relative' }}>
                    <User size={18} style={{
                      position: 'absolute',
                      left: 'var(--space-md)',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-muted)'
                    }} />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="John Smith"
                      required={isSignup}
                      data-testid="signup-name-input"
                      style={{
                        width: '100%',
                        padding: 'var(--space-md) var(--space-md) var(--space-md) calc(var(--space-md) * 2 + 18px)',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--glass-border)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--text-primary)',
                        fontSize: '0.9375rem',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Email Input */}
              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-xs)',
                  color: 'var(--text-secondary)',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}>
                  Email
                </label>
                <div style={{ position: 'relative' }}>
                  <Mail size={18} style={{
                    position: 'absolute',
                    left: 'var(--space-md)',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)'
                  }} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    data-testid="login-email-input"
                    style={{
                      width: '100%',
                      padding: 'var(--space-md) var(--space-md) var(--space-md) calc(var(--space-md) * 2 + 18px)',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--glass-border)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      fontSize: '0.9375rem',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                  />
                </div>
              </div>

              {/* Password Input */}
              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-xs)',
                  color: 'var(--text-secondary)',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}>
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <Lock size={18} style={{
                    position: 'absolute',
                    left: 'var(--space-md)',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)'
                  }} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    data-testid="login-password-input"
                    style={{
                      width: '100%',
                      padding: 'var(--space-md) calc(var(--space-md) * 2 + 18px) var(--space-md) calc(var(--space-md) * 2 + 18px)',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--glass-border)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      fontSize: '0.9375rem',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: 'var(--space-md)',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      padding: '4px'
                    }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </>
          ) : (
            /* 2FA Input */
            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)',
                marginBottom: 'var(--space-lg)',
                padding: 'var(--space-md)',
                background: 'rgba(99, 102, 241, 0.1)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid rgba(99, 102, 241, 0.2)'
              }}>
                <Shield size={20} style={{ color: 'var(--accent-primary)' }} />
                <div>
                  <div style={{ fontWeight: '500', color: 'var(--text-primary)', fontSize: '0.875rem' }}>
                    Two-Factor Authentication Required
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                    Enter the code from your authenticator app
                  </div>
                </div>
              </div>
              
              <label style={{
                display: 'block',
                marginBottom: 'var(--space-xs)',
                color: 'var(--text-secondary)',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                Authentication Code
              </label>
              <input
                type="text"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                required
                autoFocus
                data-testid="login-2fa-input"
                style={{
                  width: '100%',
                  padding: 'var(--space-lg)',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)',
                  fontSize: '1.5rem',
                  textAlign: 'center',
                  letterSpacing: '0.5rem',
                  outline: 'none'
                }}
                maxLength={6}
              />
              
              <button
                type="button"
                onClick={handleBack}
                style={{
                  marginTop: 'var(--space-md)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Use different account
              </button>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !email || !password || (isSignup && !name) || (requires2FA && twoFactorCode.length < 6)}
            className="btn btn-primary"
            data-testid="login-submit-btn"
            style={{
              width: '100%',
              padding: 'var(--space-md)',
              fontSize: '1rem',
              fontWeight: '600',
              marginBottom: 'var(--space-md)'
            }}
          >
            {loading ? (
              <>
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                {requires2FA ? 'Verifying...' : isSignup ? 'Creating account...' : 'Signing in...'}
              </>
            ) : (
              requires2FA ? 'Verify & Sign In' : isSignup ? 'Create Account' : 'Sign In'
            )}
          </button>

          {/* Forgot Password Link (Login only) */}
          {!isSignup && !requires2FA && (
            <div style={{ textAlign: 'center', marginBottom: 'var(--space-md)' }}>
              <Link
                to="/forgot-password"
                style={{
                  color: 'var(--accent-primary)',
                  fontSize: '0.875rem',
                  textDecoration: 'none'
                }}
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </Link>
            </div>
          )}
        </form>

        {/* Toggle Signup/Login */}
        {!requires2FA && (
          <div style={{
            marginTop: 'var(--space-lg)',
            paddingTop: 'var(--space-lg)',
            borderTop: '1px solid var(--glass-border)',
            textAlign: 'center'
          }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              {isSignup ? 'Already have an account?' : "Don't have an account?"}
              <button
                type="button"
                onClick={toggleMode}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent-primary)',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  marginLeft: 'var(--space-xs)',
                  textDecoration: 'underline'
                }}
                data-testid="toggle-auth-mode-btn"
              >
                {isSignup ? 'Sign in' : 'Sign up'}
              </button>
            </p>
          </div>
        )}

        {/* Founder Bootstrap Section */}
        {!founderExists && !requires2FA && !isSignup && (
          <div style={{
            marginTop: 'var(--space-lg)',
            paddingTop: 'var(--space-lg)',
            borderTop: '1px solid var(--glass-border)'
          }}>
            <p style={{
              textAlign: 'center',
              color: 'var(--text-muted)',
              fontSize: '0.8125rem',
              marginBottom: 'var(--space-md)'
            }}>
              First time? Set up your Founder account
            </p>
            <button
              onClick={handleBootstrapFounder}
              disabled={loading}
              className="btn btn-secondary"
              data-testid="bootstrap-founder-btn"
              style={{ width: '100%', padding: 'var(--space-md)' }}
            >
              {loading ? (
                <>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Setting up...
                </>
              ) : (
                'Bootstrap Founder Account'
              )}
            </button>
          </div>
        )}

        {/* Quick Fill for Development */}
        {founderExists && !requires2FA && !isSignup && (
          <div style={{
            marginTop: 'var(--space-md)',
            textAlign: 'center'
          }}>
            <button
              type="button"
              onClick={fillFounderCredentials}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                fontSize: '0.75rem',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
              data-testid="fill-founder-btn"
            >
              Use Founder credentials (Dev)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;
